export enum ContentType {
  APPLICATION_JSON = "application/json",
}

export enum RequestMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
}

export enum RequestHeader {
  AUTHORIZATION = "Authorization",
  CONTENT_TYPE = "Content-Type",
  TENANT_KEY = "Tenant-Key",
  ACCEPTED_LANGUAGE = "Accept-Language",
  REQUEST_ID = "x-request-id",
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RequestBody = Record<string, any> | FormData;

export type ApiClientDiagnostics = {
  online?: boolean;
  visibilityState?: string;
  connectionEffectiveType?: string;
  connectionType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
};

export type ApiResourceTiming = {
  durationMs?: number;
  bodySizeAvailable?: boolean;
  transferSize?: number;
  encodedBodySize?: number;
  decodedBodySize?: number;
  nextHopProtocol?: string;
};

type FailedRequestSnapshot = Omit<FetchApiOptions, "body" | "signal"> & {
  bodyType?: "form_data" | "json";
  hasBody?: boolean;
  requestId?: string;
  responseRequestId?: string;
  responseRequestIdEchoed?: boolean;
  client?: ApiClientDiagnostics;
  resourceTiming?: ApiResourceTiming;
};

export type ResponseSucceeded<T = never> = {
  response: T;
  isResponseOk: true;
  responseStatus: number;
};

export type ResponseFailed<T = never> = {
  request: FailedRequestSnapshot;
  response: T | undefined;
  isResponseOk: false;
  responseStatus: number | undefined;
  reason: string;
};

type Response<TSuccess, TError> =
  | ResponseSucceeded<TSuccess>
  | ResponseFailed<TError>;

export type RequestResponse<TSuccess, TError = never> = Promise<
  Response<TSuccess, TError>
>;

export type FetchApiOptions = {
  method: RequestMethod;
  api: string;
  shouldParseResponse?: boolean;
  body?: RequestBody;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  language?: string | null;
};

export type FetchWithErrorsOptions = FetchApiOptions & {
  canFail?: boolean;
};

export type FetchRequestOptions = Omit<FetchWithErrorsOptions, "method">;

export type FetchRequest<TSuccess, TError = never> = (
  options: FetchRequestOptions,
) => RequestResponse<TSuccess, TError>;

type ApiRequestOptions = Omit<FetchRequestOptions, "api" | "canFail">;

type ApiRequestWithParameters<TResponse, TParameters> = (
  options: TParameters & ApiRequestOptions,
) => TResponse;

type ApiRequestWithoutParameters<TResponse> = (
  options?: ApiRequestOptions,
) => TResponse;

export type ApiRequest<TResponse, TParameters> = TParameters extends never
  ? ApiRequestWithoutParameters<TResponse>
  : ApiRequestWithParameters<TResponse, TParameters>;

export type BodyWrapper<T> = {
  body: T;
};
